// src/components/charts/DoughnutQuantitesFamille.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  Plugin,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

// --- Types API ---------------------------------------------------------------
type Item = { codefamille: string; total_qte: number };
type ApiRes = {
  period: { start: string; end: string; type: 'jour' | 'mois' | 'annee'; annee: number; mois?: number | null };
  total_qte: number;
  items: Item[];
};

// --- Props -------------------------------------------------------------------
type Props = {
  /** Filtrage côté API : un des trois ensembles doit être fourni */
  date?: string;        // "YYYY-MM-DD"
  annee?: number;       // 2025
  mois?: number;        // 1..12 (seulement si annee est défini)
  /** Combien de tranches afficher; le reste est groupé en "Autres" */
  maxSlices?: number;
  /** Classe utilitaire pour wrapper (card, etc.) */
  className?: string;
  /** Titre à afficher au-dessus du donut */
  title?: string;
};

// Palette de couleurs (beaucoup pour limiter les doublons)
const PALETTE = [
  '#4f46e5','#06b6d4','#22c55e','#f59e0b','#ef4444','#a855f7','#14b8a6','#3b82f6',
  '#e11d48','#84cc16','#0ea5e9','#f97316','#64748b','#16a34a','#8b5cf6','#f43f5e',
  '#10b981','#2563eb','#d946ef','#059669','#7c3aed','#facc15','#ea580c','#0ea5e9',
];

function fmtQty(n: number) {
  // Ajuste si tu veux des décimales fixes
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(n || 0);
}

// Plugin: total au centre du donut
const centerTextPlugin = (getText: () => string): Plugin<'doughnut'> => ({
  id: 'centerText',
  afterDraw: (chart) => {
    const { ctx, chartArea } = chart;
    if (!ctx || !chartArea) return;

    const txt = getText();
    ctx.save();
    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#111827'; // gray-900
    ctx.font = '600 14px system-ui, -apple-system, Segoe UI, Roboto, Ubuntu';
    ctx.fillText(txt, centerX, centerY);
    ctx.restore();
  }
});

export default function DoughnutQuantitesFamille({
  date,
  annee,
  mois,
  maxSlices = 10,
  className = '',
  title = 'Quantités par famille',
}: Props) {
  const [payload, setPayload] = useState<ApiRes | null>(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  // Charger les données
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');

        const params: any = {};
        if (date) params.date = date;
        else if (annee && mois) { params.annee = annee; params.mois = mois; }
        else if (annee) { params.annee = annee; }
        else {
          setErr('Fournis soit "date", soit "annee", soit "annee"+"mois".');
          setLoading(false);
          return;
        }

        const res = await api.get<ApiRes>('/quantites-par-famille', { params });
        if (!cancelled) setPayload(res.data);
      } catch (e: any) {
        if (!cancelled) setErr(e?.response?.data?.error || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date, annee, mois]);

  // Prépare les données du doughnut (top N + "Autres")
  const { chartData, totalAll } = useMemo(() => {
    const empty: { chartData: ChartData<'doughnut'>; totalAll: number } = {
      chartData: { labels: [], datasets: [] },
      totalAll: 0,
    };
    if (!payload) return empty;

    const itemsSorted = [...payload.items].sort((a, b) => b.total_qte - a.total_qte);
    const top = itemsSorted.slice(0, Math.max(1, maxSlices));
    const rest = itemsSorted.slice(Math.max(1, maxSlices));

    const sumTop = top.reduce((s, it) => s + (it.total_qte || 0), 0);
    const sumRest = rest.reduce((s, it) => s + (it.total_qte || 0), 0);
    const labels = top.map(it => it.codefamille);
    const data = top.map(it => it.total_qte);

    if (sumRest > 0) {
      labels.push('Autres');
      data.push(sumRest);
    }

    const colors = labels.map((_, i) => PALETTE[i % PALETTE.length]);
    const borderColors = '#FFFFFF'
    const bgColors = colors.map(c => c + '55'); // légère transparence

    return {
      chartData: {
        labels,
        datasets: [
          {
            label: 'Quantité',
            data,
            backgroundColor: bgColors,
            borderColor: borderColors,
            borderWidth: 1.5,
            hoverOffset: 6,
          },
        ],
      },
      totalAll: sumTop + sumRest,
    };
  }, [payload, maxSlices]);

  const options: ChartOptions<'doughnut'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '50%', // “épaisseur” de l’anneau
    plugins: {
      legend: {
        position: 'right',
        labels: { usePointStyle: true },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.label || '';
            const val = typeof ctx.parsed === 'number' ? ctx.parsed : 0;
            const total = (ctx.dataset.data as number[]).reduce((s, n) => s + (n || 0), 0);
            const pct = total ? Math.round((val / total) * 100) : 0;
            return ` ${label}: ${fmtQty(val)} (${pct}%)`;
          },
        },
      },
      title: { display: false },
    },
  }), []);

  const centerText = useMemo(() => `Total\n${fmtQty(totalAll)}`, [totalAll]);

  return (
    <div className={`rounded-2xl border border-gray-200 bg-white px-5 pb-4 pt-5 dark:border-darkBorderCard dark:bg-white/[0.03] sm:px-6 sm:pt-6 ${className}`}>
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{title}</h3>
          {payload?.period && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Période : {payload.period.start} → {payload.period.end}
            </p>
          )}
        </div>
      </div>

      {/* Body */}
      {loading && <div className="text-gray-500">Chargement…</div>}
      {err && !loading && <div className="text-red-600">{err}</div>}
      {!loading && !err && chartData?.datasets?.length ? (
        <div style={{ height: 340 }}>
          <Doughnut
            data={chartData}
            options={options}
            plugins={[centerTextPlugin(() => centerText)]}
          />
        </div>
      ) : null}
    </div>
  );
}
